import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { HeroCruiseLocation } from './components/HeroCruiseLocation'
import CruiseTilePrices from 'components/Cruises/CruiseTiles/Common/CruiseTilePrices'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import ModalContext from 'contexts/ModalContext'
import CruiseModalPrices from 'components/Cruises/CruiseTiles/Common/CruiseModalPrices/CruiseModalPrices'
import CruiseModalItineraryDetails from 'components/Cruises/CruiseTiles/Common/CruiseModalItineraryDetails'
import { isCruiseOffer, isCruiseV1Offer } from 'lib/offer/offerTypes'
import { useCruiseOfferInclusions } from 'hooks/Cruise/useCruiseOfferInclusions'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import { hasDisplayablePackagePrice, isDisplayablePrice } from 'lib/offer/pricingVisibility'

const DetailContainer = styled(Group)`
  height: 100%;
  padding-top: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.desktop} {
    flex: 1;
    padding-block: ${rem(16)} ${rem(32)};
    padding-inline: ${rem(32)};
  }
`
const MinHeightGroup = styled(Group)`
  min-height: ${rem(300)};
  flex-grow: 1;
`

interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
}

function HeroCruiseOfferDetails(props: Props) {
  const { offer } = props
  const intl = useIntl()
  const view = useCruiseOfferView({ offer })
  const {
    departureCity,
    destinationCity,
    shipDetailsLabel,
    itineraryDetailsLabel,
    earliestDepartureDate,
    laterDates,
    isFlashOffer,
    isSpecialOffer,
    itinerary,
    itineraryFlash,
    offerTitle,
    isNew,
  } = view
  const showModal = useContext(ModalContext)

  const openItineraryModal = useCallback(async(e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()

    showModal(<CruiseModalItineraryDetails
      itinerary={itinerary}
      itineraryFlash={itineraryFlash}
      offerTitle={offerTitle}
      offer={offer}
    />)
  }, [itinerary, showModal, itineraryFlash, offerTitle, offer])

  const openPricesModal = useCallback(async(e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isFlashOffer) {
      const departures = 'departuresSummary' in offer ? offer.departuresSummary : undefined
      await showModal<boolean>(<CruiseModalPrices
        offerTitle={offerTitle}
        offer={offer}
        departures={departures}
      />)
    }
  }, [isFlashOffer, offer, offerTitle, showModal])

  const mainDeparture = useMemo(() => {
    if (!('mainDepartureDetails' in offer)) {
      return
    }

    return offer.mainDepartureDetails
  }, [offer])

  const { luxPlusInclusions } = useCruiseOfferInclusions({ departure: mainDeparture })
  const showStandalonePricing = isCruiseOffer(offer) && isDisplayablePrice(view.lowestPrice?.primaryPrice.total)
  const showPackagePricing = isCruiseV1Offer(offer) && !offer.bundledWithFlightsOnly && hasDisplayablePackagePrice(offer.lowestPricePackage)
  const showPricingBlock = showStandalonePricing || showPackagePricing

  const andMoreText = intl.formatMessage({
    id: 'gyFk0ZlhgZCzO_wwYYTP',
    defaultMessage: 'and more',
    description: 'Link text indicating more departure dates are available for cruise offer',
  })

  return (
    <DetailContainer direction="vertical" gap={16}>
      <TripGuard>
        <CSSBreakpoint min="desktop">
          <Group direction="horizontal-reverse">
            <BookmarkButton offer={offer} />
          </Group>
        </CSSBreakpoint>
      </TripGuard>
      <MinHeightGroup direction="vertical" verticalAlign="space-between" fullHeight gap={24}>
        <VerticalSpacer gap={12}>
          <VerticalSpacer gap={4}>
            <LabelGroup>
              {isNew && (
                <NeutralLabel>{intl.formatMessage({
                  id: 'nYzeJNVk_13EOZfkkVo7',
                  defaultMessage: 'New',
                  description: 'Urgency badge label for new cruise offers on hero tile',
                })}
                </NeutralLabel>
              )}
              {isSpecialOffer && (
                <HighlightLabel>
                  {intl.formatMessage({
                    id: 'LldTKNXBsV0csJherlHk',
                    defaultMessage: 'Special offer',
                    description: 'Promotion label for special cruise offers on hero tile',
                  })}
                </HighlightLabel>
              )}
              {isCruiseV1Offer(offer) && <OfferLabels offer={offer} />}
            </LabelGroup>

            <CruiseLuxPlusOnboardCreditLabel luxPlusInclusions={luxPlusInclusions} />

            <HeroCruiseLocation departureCity={departureCity} destinationCity={destinationCity} />

            <Heading variant="heading6" lineClamp={3} colour="normal-contrast">{offer.name}</Heading>
          </VerticalSpacer>

          <VerticalSpacer gap={4}>
            <VerticalSpacer gap={2}>
              <BodyText variant="medium" colour="light-foreground-alt">
                {shipDetailsLabel}
              </BodyText>

              <Group gap={8} direction="horizontal" verticalAlign="center" wrap="wrap">
                <BodyText variant="medium" colour="light-foreground-alt">
                  {itineraryDetailsLabel}
                </BodyText>

                {(itinerary || isFlashOffer) && (
                  <TextLink weight="regular" size="caption" onClick={openItineraryModal}>
                    <CSSBreakpoint min="desktop">{intl.formatMessage({
                      id: '4pssDzVVMxKjCo5isixk',
                      defaultMessage: 'View itinerary',
                      description: 'Link text to open cruise itinerary modal on hero tile',
                    })}
                    </CSSBreakpoint>
                    <CSSBreakpoint max="tablet"><LineInfoCircleIcon size="XS" /></CSSBreakpoint>
                  </TextLink>
                )}
              </Group>
            </VerticalSpacer>

            <BodyText variant="medium" colour="light-foreground-alt" weight="bold">
              {earliestDepartureDate && !!laterDates.length && <FormattedMessage
                id="tC30H1OWsxAOr57MJaQ-"
                defaultMessage="Departing on {date} <moreLink>{andMore}</moreLink>"
                description="Departure date with link to more dates on hero cruise offer tile"
                values={{
                  date: earliestDepartureDate,
                  andMore: andMoreText,
                  moreLink: (chunks) => isFlashOffer
                    ? <TextLink>{chunks}</TextLink>
                    : <TextLink onClick={openPricesModal}>{chunks}</TextLink>,
                }}
              />}
              {earliestDepartureDate && !laterDates.length && intl.formatMessage({
                id: 'wUUdOjZjogNoDx2qeHij',
                defaultMessage: 'Departing on {date}',
                description: 'Departure date text on hero cruise offer tile component',
              }, { date: earliestDepartureDate })}
            </BodyText>
          </VerticalSpacer>

          {isCruiseOffer(offer) && <CruiseLoyaltyPoints
            offer={offer}
            calculationType="estimate"
          />}
        </VerticalSpacer>

        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          {showPricingBlock && <>
            {showStandalonePricing && <CruiseTilePrices view={view} isStandalone />}

            {showPackagePricing && (
              <OfferTilePricing
                offer={offer}
                pkg={offer.lowestPricePackage}
                offerUrl=""
              />
            )}
          </>}

          <CSSBreakpoint min="tablet">
            <TextButton fit="desktop-full-width" kind="primary" nonInteractable>{intl.formatMessage({
              id: 'gPU7xn-36ClvL_zMoyod',
              defaultMessage: 'View offer',
              description: 'Call-to-action button text on hero cruise offer tile',
            })}
            </TextButton>
          </CSSBreakpoint>
        </Group>
      </MinHeightGroup>
    </DetailContainer>
  )
}

export default HeroCruiseOfferDetails
